import { registerPlugin } from "@capacitor/core";
import { isAndroidPlatform, isIOSPlatform } from "../utils/platformFeatures";

const RESULT_EVENT = "aileCalendarIntegrationResult";
const STORAGE_KEY = "fp:calendar-integration:v1";
const pending = new Map();
let listenerAttached = false;
const AndroidCalendarIntegration = registerPlugin("CalendarIntegration");

const makeCallbackId = () =>
  `calendar-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const attachResultListener = () => {
  if (listenerAttached || typeof window === "undefined") return;
  listenerAttached = true;
  window.addEventListener(RESULT_EVENT, (event) => {
    const detail = event?.detail || {};
    const request = pending.get(detail.callbackId);
    if (!request) return;
    pending.delete(detail.callbackId);
    window.clearTimeout(request.timer);
    if (detail.ok === false) {
      const error = new Error(detail.message || "CALENDAR_INTEGRATION_FAILED");
      error.code = detail.code || "CALENDAR_INTEGRATION_FAILED";
      request.reject(error);
      return;
    }
    request.resolve(detail.data || {});
  });
};

const sendNativeRequest = (action, payload = {}, timeoutMs = 20000) => {
  if (isAndroidPlatform()) {
    const methodByAction = {
      calendarIntegrationStatus: "status",
      calendarIntegrationRequestAccess: "requestAccess",
      calendarIntegrationListCalendars: "listCalendars",
      calendarIntegrationReadEvents: "readEvents",
      calendarIntegrationOpenSettings: "openSettings",
    };
    const method = methodByAction[action];
    if (!method) return Promise.reject(Object.assign(new Error("CALENDAR_ACTION_UNSUPPORTED"), { code: "CALENDAR_ACTION_UNSUPPORTED" }));
    return AndroidCalendarIntegration[method](payload);
  }
  if (!isIOSPlatform()) {
    return Promise.reject(Object.assign(new Error("CALENDAR_NATIVE_ONLY"), { code: "CALENDAR_NATIVE_ONLY" }));
  }
  const bridge = window?.webkit?.messageHandlers?.aileNativeBridge;
  if (!bridge?.postMessage) {
    return Promise.reject(Object.assign(new Error("CALENDAR_BRIDGE_NOT_READY"), { code: "CALENDAR_BRIDGE_NOT_READY" }));
  }
  attachResultListener();
  const id = makeCallbackId();
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      pending.delete(id);
      reject(Object.assign(new Error("CALENDAR_REQUEST_TIMEOUT"), { code: "CALENDAR_REQUEST_TIMEOUT" }));
    }, timeoutMs);
    pending.set(id, { resolve, reject, timer });
    bridge.postMessage({ action, callbackId: id, ...payload });
  });
};

const dateKey = (value) => {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : "";
};

const zonedParts = (value, timeZone) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    }).formatToParts(date).reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
    return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour}:${parts.minute}` };
  } catch (_) {
    return null;
  }
};

const previousDateKey = (value) => {
  const key = dateKey(value);
  if (!key) return "";
  const [year, month, day] = key.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() - 1);
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`;
};

export const normalizeExternalCalendarEvent = (event = {}) => {
  const startAt = String(event.startAt || "");
  const endAt = String(event.endAt || startAt);
  const zonedStart = event.allDay ? null : zonedParts(startAt, event.timeZone);
  const zonedEnd = event.allDay ? null : zonedParts(endAt, event.timeZone);
  const startDate = dateKey(event.startDate) || zonedStart?.date || dateKey(startAt);
  const exclusiveEndDate = dateKey(event.endDate) || zonedEnd?.date || dateKey(endAt);
  let endDate = exclusiveEndDate || startDate;
  if (event.allDay && startDate && exclusiveEndDate && exclusiveEndDate > startDate) {
    endDate = previousDateKey(exclusiveEndDate);
  }
  const time = event.allDay ? "" : (zonedStart?.time || startAt.match(/T(\d{2}:\d{2})/)?.[1] || "");
  return {
    id: `external:${event.provider || "apple"}:${event.calendarId || "unknown"}:${event.eventId || event.id || "unknown"}`,
    externalEventId: String(event.externalIdentifier || event.eventId || event.id || ""),
    externalCalendarId: String(event.calendarId || ""),
    source: "device",
    provider: event.provider || "apple",
    sourceTitle: event.calendarTitle || "",
    sourceColor: event.calendarColor || "#64748b",
    baslik: String(event.title || "").trim() || "Etkinlik",
    aciklama: String(event.notes || ""),
    tarih: startDate,
    endDate: endDate || startDate,
    saat: time,
    allDay: Boolean(event.allDay),
    timeZone: event.timeZone || "",
    location: event.location || "",
    url: event.url || "",
    isReadOnly: true,
    recurrenceType: event.hasRecurrenceRules ? "external" : "none",
    kategori: "Harici Takvim",
    renk: event.calendarColor || "#64748b",
    type: "externalEvent",
  };
};

export const getCalendarIntegrationPreferences = () => {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return {
      enabled: value.enabled === true,
      selectedCalendarIds: Array.isArray(value.selectedCalendarIds) ? value.selectedCalendarIds.map(String) : [],
      lastSyncedAt: value.lastSyncedAt || null,
    };
  } catch (_) {
    return { enabled: false, selectedCalendarIds: [], lastSyncedAt: null };
  }
};

export const saveCalendarIntegrationPreferences = (preferences = {}) => {
  const current = getCalendarIntegrationPreferences();
  const next = { ...current, ...preferences };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  return next;
};

export const getDeviceCalendarStatus = () => sendNativeRequest("calendarIntegrationStatus");
export const requestDeviceCalendarAccess = () =>
  sendNativeRequest("calendarIntegrationRequestAccess", {}, 60000);
export const listDeviceCalendars = () => sendNativeRequest("calendarIntegrationListCalendars");
export const openDeviceCalendarSettings = () => sendNativeRequest("calendarIntegrationOpenSettings");

export const readDeviceCalendarEvents = async ({ calendarIds, startDate, endDate }) => {
  const data = await sendNativeRequest("calendarIntegrationReadEvents", {
    calendarIds: Array.isArray(calendarIds) ? calendarIds : [],
    startDate,
    endDate,
  }, 30000);
  const events = (Array.isArray(data.events) ? data.events : [])
    .map(normalizeExternalCalendarEvent)
    .filter((event) => event.tarih && event.externalEventId);
  return Promise.all(events.map(async (event) => ({
    ...event,
    externalImportKey: await createExternalImportKey(event),
  })));
};

export const createExternalImportKey = async (event = {}) => {
  const raw = [event.provider, event.externalCalendarId, event.externalEventId, event.tarih, event.saat].map((value) => String(value || "")).join("|");
  if (typeof crypto !== "undefined" && crypto.subtle && typeof TextEncoder !== "undefined") {
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(raw));
    return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, "0")).join("");
  }
  return `fallback:${raw}`;
};

export const isDeviceCalendarSupported = () => isIOSPlatform() || isAndroidPlatform();
